export const hexToRgb = (hex: string) => {
  const rgbChar = ['r', 'g', 'b'] as const;

  const normal = hex.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  type Return = Record<(typeof rgbChar)[number], number>;
  if (normal) {
    return normal.slice(1).reduce<Return>((a, e, i) => {
      a[rgbChar[i]] = parseInt(e, 16);
      return a;
    }, {} as Return);
  }

  const shorthand = hex.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i);
  if (shorthand) {
    return shorthand.slice(1).reduce<Return>((a, e, i) => {
      a[rgbChar[i]] = 0x11 * parseInt(e, 16);
      return a;
    }, {} as Return);
  }

  return null;
};

export const hexToRgbAString = (hex: string, a: number) => {
  const rgb = hexToRgb(hex);

  if (!rgb) return hex;
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${a})`;
};

const tealColors = {
  teal1400: '#E0F5F4',
  teal1300: '#C7EEEC',
  teal1200: '#A5E4E1',
  teal1100: '#7DD8D3',
  teal1000: '#4EC9C3',
  teal900: '#19B9B1',
  teal800: '#15A59E',
  teal700: '#13918B',
  teal600: '#107E79',
  teal500: '#0E6C67',
  teal400: '#0C5A57',
  teal300: '#0A4A46',
  teal200: '#083A38',
  teal100: '#062C2B',
} as const;

const indigoColors = {
  indigo1400: '#EFF0FE',
  indigo1300: '#E2E5FD',
  indigo1200: '#D2D6FC',
  indigo1100: '#BEC5FB',
  indigo1000: '#AAB3FA',
  indigo900: '#949FF9',
  indigo800: '#7D8BF8',
  indigo700: '#6576F6',
  indigo600: '#5766D9',
  indigo500: '#4A57BB',
  indigo400: '#3E499C',
  indigo300: '#323B7E',
  indigo200: '#272E62',
  indigo100: '#1E234B',
} as const;

const redColors = {
  red1400: '#FEEEEB',
  red1300: '#FEE0DB',
  red1200: '#FDCDC6',
  red1100: '#FCB9AE',
  red1000: '#FBA091',
  red900: '#FA8472',
  red800: '#F9634C',
  red700: '#DC5743',
  red600: '#BF4C3A',
  red500: '#A34132',
  red400: '#89362A',
  red300: '#702C22',
  red200: '#57231B',
  red100: '#431B15',
} as const;

const greenColors = {
  green1400: '#E4F5EC',
  green1300: '#CDEEDD',
  green1200: '#B1E3C9',
  green1100: '#8FD8B2',
  green1000: '#6ACA99',
  green900: '#3FBB7B',
  green800: '#22A964',
  green700: '#1E9558',
  green600: '#1A814C',
  green500: '#166E41',
  green400: '#135D37',
  green300: '#0F4B2C',
  green200: '#0C3B23',
  green100: '#092D1A',
} as const;

const orangeColors = {
  orange1400: '#FCEEE7',
  orange1300: '#FAE1D4',
  orange1200: '#F7D1BB',
  orange1100: '#F4BD9F',
  orange1000: '#F1A67E',
  orange900: '#ED8E5B',
  orange800: '#E87333',
  orange700: '#CE642B',
  orange600: '#B35725',
  orange500: '#994B20',
  orange400: '#803F1B',
  orange300: '#693316',
  orange200: '#522811',
  orange100: '#3D1E0D',
} as const;

const purpleColors = {
  purple1400: '#F4EFFD',
  purple1300: '#EBE2FB',
  purple1200: '#E0D2F8',
  purple1100: '#D3C0F5',
  purple1000: '#C4ACF2',
  purple900: '#B696EF',
  purple800: '#A680EB',
  purple700: '#9669E8',
  purple600: '#8454DD',
  purple500: '#7148BD',
  purple400: '#5F3C9F',
  purple300: '#4E3182',
  purple200: '#3C2664',
  purple100: '#2E1D4E',
} as const;

const grayscaleColors = {
  grayscaleWhite: '#ffffff',
  grayscale50: '#fcfcfc',
  grayscale100: '#f5f5f5',
  grayscale150: '#EFEFEF',
  grayscale200: '#E8E8E8',
  grayscale250: '#DFDFDF',
  grayscale300: '#C8C8C8',
  grayscale350: '#B7B7B7',
  grayscale400: '#949494',
  grayscale450: '#777777',
  grayscale500: '#616161',
  grayscale550: '#555555',
  grayscale600: '#3F3F3F',
  grayscale650: '#383838',
  grayscale700: '#2A2A2A',
  grayscale750: '#1F1F1F',
  grayscale800: '#1A1A1A',
  grayscale850: '#111111',
  grayscaleBlack: '#000000',
} as const;

export const colors = {
  ...tealColors,
  ...indigoColors,
  ...redColors,
  ...greenColors,
  ...orangeColors,
  ...purpleColors,
  ...grayscaleColors,
} as const;

export const thematicColors = {
  primary: {
    air: colors.teal1200,
    light: colors.teal1100,
    regular: colors.teal1000,
    medium: colors.teal900,
    heavy: colors.teal800,
  },
  secondary: {
    air: colors.indigo1100,
    light: colors.indigo1000,
    regular: colors.indigo900,
    medium: colors.indigo800,
    heavy: colors.indigo700,
  },
};

export const semanticColors = {
  danger: {
    air: colors.red1100,
    light: colors.red1000,
    regular: colors.red900,
    medium: colors.red800,
    heavy: colors.red700,
  },
  warning: {
    air: colors.orange1100,
    light: colors.orange1000,
    regular: colors.orange900,
    medium: colors.orange800,
    heavy: colors.orange700,
  },
  success: {
    air: colors.green1100,
    light: colors.green1000,
    regular: colors.green900,
    medium: colors.green800,
    heavy: colors.green700,
  },
} as const;

export const overlayColors = {
  white: {
    1: `${hexToRgbAString(grayscaleColors.grayscaleWhite, 0.05)}`,
    2: `${hexToRgbAString(grayscaleColors.grayscaleWhite, 0.07)}`,
    4: `${hexToRgbAString(grayscaleColors.grayscaleWhite, 0.09)}`,
    8: `${hexToRgbAString(grayscaleColors.grayscaleWhite, 0.12)}`,
    16: `${hexToRgbAString(grayscaleColors.grayscaleWhite, 0.15)}`,
  },
  primary: {
    2: `${hexToRgbAString(thematicColors.primary.regular, 0.07)}`,
    4: `${hexToRgbAString(thematicColors.primary.regular, 0.09)}`,
    8: `${hexToRgbAString(thematicColors.primary.regular, 0.12)}`,
    16: `${hexToRgbAString(thematicColors.primary.regular, 0.15)}`,
    24: `${hexToRgbAString(thematicColors.primary.regular, 0.16)}`,
  },
  secondary: {
    2: `${hexToRgbAString(thematicColors.secondary.regular, 0.07)}`,
    4: `${hexToRgbAString(thematicColors.secondary.regular, 0.09)}`,
    8: `${hexToRgbAString(thematicColors.secondary.regular, 0.12)}`,
    16: `${hexToRgbAString(thematicColors.secondary.regular, 0.15)}`,
    24: `${hexToRgbAString(thematicColors.secondary.regular, 0.16)}`,
  },
  danger: {
    2: `${hexToRgbAString(semanticColors.danger.regular, 0.07)}`,
    4: `${hexToRgbAString(semanticColors.danger.regular, 0.09)}`,
    8: `${hexToRgbAString(semanticColors.danger.regular, 0.12)}`,
    16: `${hexToRgbAString(semanticColors.danger.regular, 0.15)}`,
    24: `${hexToRgbAString(semanticColors.danger.regular, 0.16)}`,
  },
  warning: {
    2: `${hexToRgbAString(semanticColors.danger.regular, 0.07)}`,
    4: `${hexToRgbAString(semanticColors.danger.regular, 0.09)}`,
    8: `${hexToRgbAString(semanticColors.danger.regular, 0.12)}`,
    16: `${hexToRgbAString(semanticColors.danger.regular, 0.15)}`,
    24: `${hexToRgbAString(semanticColors.danger.regular, 0.16)}`,
  },
  success: {
    2: `${hexToRgbAString(semanticColors.success.regular, 0.07)}`,
    4: `${hexToRgbAString(semanticColors.success.regular, 0.09)}`,
    8: `${hexToRgbAString(semanticColors.success.regular, 0.12)}`,
    16: `${hexToRgbAString(semanticColors.success.regular, 0.15)}`,
    24: `${hexToRgbAString(semanticColors.success.regular, 0.16)}`,
  },
};

export const colorTokens = {
  bgDefault: colors.grayscaleWhite,
  bgWeak: hexToRgbAString(colors.grayscaleBlack, 0.04),
  bgPrimary: '#F0FFFC',
  bgPrimaryAccent: '#D1F0EB',
  textDefault: colors.grayscaleBlack,
  textDisabled: 'rgba(0,0,0,0.25)',
  textDescription: hexToRgbAString(colors.grayscaleBlack, 0.45),
  textPlaceholder: hexToRgbAString(colors.grayscaleBlack, 0.25),
  textPrimary: '#19B9B1',
  textPrimaryAccent: thematicColors.primary.medium,
  textSuccess: semanticColors.success.medium,
  textDanger: semanticColors.danger.regular,
  tooltipIcon: 'rgba(0,0,0,0.45)',
  borderDefault: colors.grayscale200,
  borderSubtle: hexToRgbAString(colors.grayscaleBlack, 0.15),
  borderPrimary: thematicColors.primary.light,
  iconPrimary: thematicColors.primary.regular,
  iconSuccess: semanticColors.success.medium,
  iconDanger: semanticColors.danger.regular,
  iconWarning: '#FAAD14',
  iconDefault: hexToRgbAString(colors.grayscaleBlack, 0.45),
  iconPlaceholder: hexToRgbAString(colors.grayscaleBlack, 0.25),
  colorSplit: 'rgba(0, 0, 0, 0.06)',
  colorFillAlter: 'rgba(0, 0, 0, 0.02)',
  colorText: 'rgba(0, 0, 0, 0.88)',
};
